import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { connect } from 'react-redux';
import { Button, Caption1Stronger, Caption2, Divider, Label, MessageBar, MessageBarBody, MessageBarTitle, Popover, PopoverSurface, PopoverTrigger, Text, Tooltip } from '@fluentui/react-components';

// component functions
import * as ComponentFunctions from './Protocol.Activity.Functions';

//icond
import { ArrowSync16Filled, ArrowSync16Regular, ArrowSync20Filled, Branch16Regular, CalendarAdd16Regular, CalendarMultiple16Regular, Column20Regular, ColumnTriple20Regular, FullScreenMaximize20Regular, FullScreenMinimize20Regular, LinkMultiple16Regular, Note16Regular, People16Regular, Person16Regular, SubtractCircle16Regular, CalendarSettingsRegular, Call12Filled, Call16Filled, Call16Regular, Delete16Regular, DocumentBulletListMultiple20Regular, Edit16Regular, Phone16Regular, Phone28Regular, PhoneArrowRightFilled, SyncOff16Regular, Warning16Filled, Warning16Regular, Warning20Filled } from '@fluentui/react-icons';

// styles
import './Protocol.Activity.Styles.css';

// models
import { ProtocolPatient } from '../../../models/Entities/Protocols/ProtocolPatient';
import { PatientVisit } from '../../../models/Entities/Protocols/PatientVisist';
import { ProtocolPatientContractBranch } from '../../../models/Entities/Protocols/ProtocolPatientContractBranch';
import Moment from 'react-moment';
import StageFixFormComponent from '../../Contracts/Stages/StageFixForm/StageFix.Form.Component';
import { Callout, DirectionalHint, Separator } from '@fluentui/react';
import ProtocolPatientReferrerComponent from './ProtocolPatientReferrer/Protocol.PatientReferrer.Component';
import { ContractBranch } from '../../../models/Entities/Contracts/ContractBranch';
import ContractBranchSelectorComponent from '../../../commons/component/Inputs/ContractBranchSelector/ContractBranchSelector.Component';
import DateRangeSelectorComponent from '../../../commons/component/Inputs/DateRangeSelector/DateRangeSelector.Component';
import ConfirmationModalComponent from '../../../commons/component/ConfirmationModal/ConformationModal.Component';
import VisitConceptFormComponent from '../VisitConceptForm/VisitConcept.Form.Component';


// how long the extra-protocol columns take to fold; the CSS transition uses the same budget
const EXTRA_COLUMNS_ANIMATION_MS = 240;

// the grid keeps covering the screen while it fades out, so the CSS exit animation can play
const MAXIMIZE_ANIMATION_MS = 170;

// the statuses of the summary, in the same order as the legend, paired with their legend swatch
const VISIT_STATUS_SUMMARY: { status: number; swatch: string }[] = [
    { status: 1, swatch: 'completed' },
    { status: 5, swatch: 'autocompleted' },
    { status: 0, swatch: 'started' },
    { status: 4, swatch: 'changed' },
    { status: 2, swatch: 'skipped' },
    { status: 3, swatch: 'failed' }
];

const PATIENT_COLUMN_MIN_WIDTH = 220;
const PATIENT_COLUMN_MAX_WIDTH = 560;
const PATIENT_COLUMN_DEFAULT_WIDTH = 272;

const clampPatientColumnWidth = (width: number): number =>
    Math.max(PATIENT_COLUMN_MIN_WIDTH, Math.min(PATIENT_COLUMN_MAX_WIDTH, width));

const ProtocolActivityComponent = (props: ComponentFunctions.Props) => {
        type DisplayStageColumn = ComponentFunctions.internalStage;

    const { t } = useTranslation();

    const [isOpen, setIsOpen] = useState(false);
    const openPanel = () => setIsOpen(true);
    const dismissPanel = () => setIsOpen(false);

    const [branchPopoverPatient, setBranchPopoverPatient] = useState<string | null>(null);
    const [selectedPatientBranchMap, setSelectedPatientBranchMap] = useState<Record<string, ContractBranch[]>>({});
    const [patientBranchDateRange, setPatientBranchDateRange] = useState<Record<string, { dateFrom?: Date; dateTo?: Date }>>({});
    const [editingBranchAssignment, setEditingBranchAssignment] = useState<{ patient: string; id: string } | null>(null);
    const [savingBranchPatient, setSavingBranchPatient] = useState<string | null>(null);
    const [branchSaveMessage, setBranchSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [activeBranchGridKey, setActiveBranchGridKey] = useState<string>('');
    const [visitConceptDrawer, setVisitConceptDrawer] = useState<{ visitId: number; patientId: string } | null>(null);
    const [isMaximized, setIsMaximized] = useState(false);
    // isMaximized is the intent; these two keep the overlay alive while it animates in and out
    const [isMaximizedShellVisible, setIsMaximizedShellVisible] = useState(false);
    const [isMaximizedShellClosing, setIsMaximizedShellClosing] = useState(false);
    const [hideExtraProtocolVisits, setHideExtraProtocolVisits] = useState(false);
    // hideExtraProtocolVisits is the intent; these two drive the fold: the columns stay mounted
    // while they collapse, and are only removed once the transition has finished
    const [areExtraColumnsMounted, setAreExtraColumnsMounted] = useState(true);
    const [areExtraColumnsCollapsed, setAreExtraColumnsCollapsed] = useState(false);

    const [currentPatiens, setCurrentPatients] = useState<string[]>([]);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [refreshRequested, setRefreshRequested] = useState(false);
    const [patientColumnWidth, setPatientColumnWidth] = useState<number>(PATIENT_COLUMN_DEFAULT_WIDTH);
    const [isResizingPatientColumn, setIsResizingPatientColumn] = useState(false);
    const branchTabsRef = useRef<HTMLDivElement | null>(null);
    const [stageHeaderCallout, setStageHeaderCallout] = useState<{
        targetId: string;
        stageTitle: string;
        stageDescription: string;
        relations: string[];
    } | null>(null);

    useEffect(() => {
        props.GetMedicalProtocolById(props.ProtocolId);
        props.GetAllPractitioners(1, 1000, '');
    }, [props.ProtocolId]);

    useEffect(() => {
        if (hideExtraProtocolVisits) {
            setAreExtraColumnsCollapsed(true);
            const timer = window.setTimeout(() => setAreExtraColumnsMounted(false), EXTRA_COLUMNS_ANIMATION_MS);

            return () => window.clearTimeout(timer);
        }

        setAreExtraColumnsMounted(true);
        // mounted collapsed for one frame, so the browser has a width to expand from
        const frame = window.requestAnimationFrame(() => setAreExtraColumnsCollapsed(false));

        return () => window.cancelAnimationFrame(frame);
    }, [hideExtraProtocolVisits]);

    useEffect(() => {
        if (isMaximized) {
            setIsMaximizedShellClosing(false);
            setIsMaximizedShellVisible(true);

            return;
        }

        if (!isMaximizedShellVisible) {
            return;
        }

        setIsMaximizedShellClosing(true);
        const timer = window.setTimeout(() => {
            setIsMaximizedShellVisible(false);
            setIsMaximizedShellClosing(false);
        }, MAXIMIZE_ANIMATION_MS);

        return () => window.clearTimeout(timer);
    }, [isMaximized, isMaximizedShellVisible]);

    // the toolbar is covered by the maximized grid, so Escape has to be a way out too, and the page
    // behind stays locked so only the grid scrolls
    useEffect(() => {
        if (!isMaximizedShellVisible) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsMaximized(false);
            }
        };

        document.body.classList.add('pa-body--locked');
        window.addEventListener('keydown', onKeyDown);

        return () => {
            document.body.classList.remove('pa-body--locked');
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [isMaximizedShellVisible]);

    useEffect(() => {
        if (props.Protocol?.patientVisits) {
            // the rows of the grid: deduped and in patient-number order, not in the order the
            // visits happen to come back from the API
            setCurrentPatients(ComponentFunctions.WithoutNoteVisits(props.Protocol.patientVisits).map((p: PatientVisit) => p.numberInProtocol).reduce(
                (acc: string[], current: string) => {
                    if (!acc.includes(current)) acc.push(current);
                    return acc;
                }, []).sort(ComponentFunctions.ComparePatientNumbers));
        }
    }, [props.Protocol?.patientVisits]);

    useEffect(() => {
        if (props.Protocol?.id) {
            props.GetAllProtocolContractAssociations(props.Protocol.id);
            props.GetProtocolPatients(props.ProtocolId, null);
            props.GetProtocolPatientContractBranches(props.ProtocolId);
            props.GetPatientVisitByProtocolId(props.ProtocolId);
            props.GetVisitsByProtocolId(props.ProtocolId);
            props.GetHcProtocolPatientsByProtocolId(props.ProtocolId);
        }
    }, [props.Protocol?.id]);

    useEffect(() => {
        if (props.Protocol?.contractAssociation?.contractId) {
            props.GetContractById(props.Protocol.contractAssociation.contractId);
            props.GetAllContractsBranchs(props.Protocol.contractAssociation.contractId);
        }
    }, [props.Protocol?.contractAssociation?.contractId]);

    useEffect(() => {
        props.Contract?.id && props.GetAllContractsStage(props.Contract!.id!);
    }, [props.Contract?.id]);

    // Las notas entran a la grilla, pero en su propia columna: no son visitas, no
    // cuentan como actividad y se marcan distinto (ver BuildNoteVisitColumns).
    const patientVisits = useMemo(
        () => props.PatientVisits || [],
        [props.PatientVisits]
    );

    const contractStageColumns = useMemo(
        () => ComponentFunctions.GetRealStages(props.Stages, props.Visits, patientVisits),
        [props.Stages, props.Visits, patientVisits]
    );

    // Every branch that really exists for this contract, from whichever source has it loaded: the
    // addendums, the contract payload or the branch store. It is deliberately NOT narrowed to the
    // active addendum — the grid shows the stages of the whole contract, so narrowing here turned
    // the branches of any other addendum into false orphans, and an addendum that had not loaded
    // its branches wiped out every real branch tab.
    const contractBranches = useMemo(() => {
        const branchesFromAddendums = (props.Contract?.addendums || []).flatMap((addendum) => addendum.branches || []);
        const branchesFromContract = props.Contract?.branches || [];
        const branchesFromStore = (props.ContractBranches || []).filter(
            (branch) => !props.ContractId || branch.contractId === props.ContractId
        );

        return [...branchesFromAddendums, ...branchesFromContract, ...branchesFromStore]
            .filter((branch, index, array) =>
                index === array.findIndex((candidate) => candidate.id === branch.id)
            );
    }, [props.Contract, props.ContractBranches, props.ContractId]);

    // Branch ids that still exist on the contract. Deleting a branch leaves its id orphaned on the
    // stages (and patient assignments) that referenced it.
    const knownBranchIds = useMemo(
        () => new Set(
            contractBranches
                .map((branch) => Number(branch.id))
                .filter((branchId) => Number.isFinite(branchId))
        ),
        [contractBranches]
    );

    // The branch store is global: right after navigating it can still hold another contract's
    // branches, and taking that as "loaded" would report every branch of this one as deleted.
    const isBranchListForThisContract = useMemo(
        () => (props.ContractBranches || []).every((branch) => !props.ContractId || branch.contractId === props.ContractId),
        [props.ContractBranches, props.ContractId]
    );

    // Knowing one real branch is already enough to tell an orphan apart. With none, only a finished
    // fetch of THIS contract proves the list is really empty and every branchId left is an orphan.
    const areContractBranchesLoaded = knownBranchIds.size > 0
        || (props.ContractBranchesLoaded
            && isBranchListForThisContract
            && !props.IsLoadingContract
            && !props.IsLoadingContractBranches);

    const isOrphanBranchId = useCallback((branchId?: number | string | null): boolean => {
        if (branchId === undefined || branchId === null || !areContractBranchesLoaded) {
            return false;
        }

        const numericBranchId = Number(branchId);

        return Number.isFinite(numericBranchId) && !knownBranchIds.has(numericBranchId);
    }, [knownBranchIds, areContractBranchesLoaded]);

    // Stages whose branch was deleted keep a dangling branchId; flag them so they fall into the
    // "Sin rama" tab instead of generating one phantom tab per missing branch id.
    const visits = useMemo(() => {
        const columns = contractStageColumns.map((stage) => (
            isOrphanBranchId(stage.branchId)
                ? { ...stage, isOrphanBranch: true, orphanBranchId: Number(stage.branchId) }
                : stage
        ));

        // Vista de comparación: sólo las columnas que todos los pacientes comparten.
        // Se van tanto las visitas no programadas como las notas: las dos son
        // columnas de un paciente en particular y desalinean la comparación.
        return areExtraColumnsMounted
            ? columns
            : columns.filter((stage) => !stage.isExtraProtocolVisit && !stage.isNoteVisit);
    }, [contractStageColumns, isOrphanBranchId, areExtraColumnsMounted]);

    // the maximized shell only renders while there are visits: without this the toolbar would be
    // hidden with no way back if a refresh returned nothing
    useEffect(() => {
        if (isMaximized && !(visits?.length > 0)) {
            setIsMaximized(false);
        }
    }, [isMaximized, visits]);

    const ResetInfo = () => {
        setRefreshRequested(true);
        setIsRefreshing(true);

        props.Contract?.id && props.GetAllContractsStage(props.Contract!.id!);
        props.GetProtocolPatients(props.ProtocolId, null);
        props.GetProtocolPatientContractBranches(props.ProtocolId);
        props.GetPatientVisitByProtocolId(props.ProtocolId);
        props.GetVisitsByProtocolId(props.ProtocolId);
        props.GetHcProtocolPatientsByProtocolId(props.ProtocolId);
    };

    useEffect(() => {
        if (!refreshRequested) {
            return;
        }

        if (!props.IsGettingVisits && !props.IsLoadingHcProtocolPatients) {
            setIsRefreshing(false);
            setRefreshRequested(false);
        }
    }, [refreshRequested, props.IsGettingVisits, props.IsLoadingHcProtocolPatients]);


    // Move hook call to top-level (was previously called inside VisitGrid causing hook order changes)
    const styles = ComponentFunctions.useStyles();

    const VisitGrid = (stage: ComponentFunctions.internalStage, visit: PatientVisit | null, patient: ProtocolPatient | null) => {

        const hasAlerts = (stage.isRemote && visit !== null && visit.visitType === 0)
            || (stage.isRemote && stage.remoteWarning)
            || (ComponentFunctions.IsOutOfContractStage(stage))
            || !!stage.isOrphanBranch;

        const orphanBranchMessage = t('protocolActivity.table.orphanBranchDetail', {
            branchId: stage.orphanBranchId ?? stage.branchId,
            defaultValue: 'La rama asociada a esta etapa (#{{branchId}}) fue eliminada del contrato, por eso la visita figura sin rama.'
        });

        if (visit) {
            return (
                <div className='visitDetail'>
                    {/* Header: título + modalidad */}
                    <div className='visitDetail__header'>
                        <Text weight='semibold' size={300}>{visit.title}</Text>
                        <span className='visitDetail__typeTag'>
                            {visit.visitType === 0 ? <Phone16Regular /> : <Call16Regular />}
                            {visit.visitType === 0 ? 'Presencial' : 'Telefónica'}
                        </span>
                    </div>

                    {/* Estado */}
                    <div className='visitDetail__status'>
                        <span className={'VisitActivity cirle '.concat(ComponentFunctions.GetVisitStatusByVisit(visit.status)).concat(!stage.stageId ? ' warning' : '')} />
                        <Text weight='semibold'>{ComponentFunctions.GetVisitStatusDescription(visit.status)}</Text>
                    </div>

                    <Divider style={{ width: 'auto' }} />

                    {/* Datos del paciente */}
                    <div className='visitDetail__fields'>
                        <div className='visitDetail__field'>
                            <Caption2>Paciente</Caption2>
                            <Caption1Stronger>{visit.numberInProtocol}</Caption1Stronger>
                        </div>
                        <div className='visitDetail__field'>
                            <Caption2>Fecha de realización</Caption2>
                            <Caption1Stronger><Moment date={visit.date} format='DD/MM/YYYY hh:mm a' /></Caption1Stronger>
                        </div>
                        <div className='visitDetail__field'>
                            <Caption2>Profesional médico</Caption2>
                            <Caption1Stronger>{props.GetPractitioner(visit.createdById)?.fullName || 'No encontrado'}</Caption1Stronger>
                        </div>
                        <ProtocolPatientReferrerComponent
                            numberInProtocol={visit.numberInProtocol}
                            protocolPatients={props.ProtocolPatients}
                        />
                        {/* { patient && <div className='visitDetail__field'>
                            <Caption2>Profesional médico derivador</Caption2>
                            <Text>{patient.medicalPractitionerId}</Text>
                            <Caption1Stronger>{props.GetPractitioner(patient.medicalPractitionerId)?.fullName || 'No encontrado'}</Caption1Stronger>
                        </div>} */}
                    </div>

                    {/* Conceptos de visita */}
                    <div className='visitDetail__actions'>
                        <Button
                            appearance='primary'
                            icon={<DocumentBulletListMultiple20Regular />}
                            onClick={() => setVisitConceptDrawer({ visitId: visit.id, patientId: visit.numberInProtocol })}
                        >
                            Ver visita
                        </Button>
                    </div>

                    {/* Alertas agrupadas */}
                    {hasAlerts && (
                        <div className='visitDetail__alerts'>
                            {stage.isRemote && visit.visitType === 0 && (
                                <MessageBar intent='warning'>
                                    <MessageBarBody>Esta visita es remota y se realizó de forma presencial.</MessageBarBody>
                                </MessageBar>
                            )}
                            {stage.isRemote && stage.remoteWarning && (
                                <MessageBar intent='warning'>
                                    <MessageBarBody>Esta visita es remota; el contrato y el template definen modalidad presencial.</MessageBarBody>
                                </MessageBar>
                            )}
                            {/* an unscheduled visit is not "off contract": it is a category of its own */}
                            {ComponentFunctions.IsOutOfContractStage(stage) && (
                                <MessageBar intent='warning'>
                                    <MessageBarBody>
                                        <MessageBarTitle>Atención: </MessageBarTitle>
                                        Esta visita fue realizada, pero no está definida en el contrato.
                                    </MessageBarBody>
                                </MessageBar>
                            )}
                            {stage.isOrphanBranch && (
                                <MessageBar intent='warning'>
                                    <MessageBarBody>
                                        <MessageBarTitle>Rama eliminada: </MessageBarTitle>
                                        {orphanBranchMessage}
                                    </MessageBarBody>
                                </MessageBar>
                            )}
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div className='visitDetail'>
                {/* Header: nombre de etapa + modalidad */}
                <div className='visitDetail__header'>
                    <Text weight='semibold' size={300}>{stage.stageName}</Text>
                    {/* an extra-protocol visit has no modality of its own: it depends on the patient */}
                    {!stage.isExtraProtocolVisit && !stage.isNoteVisit && (
                        <span className='visitDetail__typeTag'>
                            {stage.isRemote ? <Call16Regular /> : <Phone16Regular />}
                            {stage.isRemote ? 'Telefónica' : 'Presencial'}
                        </span>
                    )}
                </div>

                {/* Estado vacío */}
                <div className='visitDetail__emptyState'>
                    <SyncOff16Regular />
                    <Text>Visita no realizada</Text>
                </div>

                {((ComponentFunctions.IsOutOfContractStage(stage)) || stage.isOrphanBranch) && (
                    <div className='visitDetail__alerts'>
                        {ComponentFunctions.IsOutOfContractStage(stage) && (
                            <MessageBar intent='error'>
                                <MessageBarBody>
                                    <MessageBarTitle>Atención: </MessageBarTitle>
                                    Esta visita no fue realizada y no está definida en el contrato.
                                </MessageBarBody>
                            </MessageBar>
                        )}
                        {stage.isOrphanBranch && (
                            <MessageBar intent='warning'>
                                <MessageBarBody>
                                    <MessageBarTitle>Rama eliminada: </MessageBarTitle>
                                    {orphanBranchMessage}
                                </MessageBarBody>
                            </MessageBar>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const getReferrerNameByPatient = (patientNumberInProtocol: string): string | undefined => {
        const normalizedPatientNumber = patientNumberInProtocol?.trim().toLowerCase();
        const protocolPatient = props.ProtocolPatients.find(
            (item) => item.numberInProtocol?.trim().toLowerCase() === normalizedPatientNumber && !!item.medicalPractitionerId
        );

        if (!protocolPatient?.medicalPractitionerId) {
            return undefined;
        }

        return props.GetPractitioner(protocolPatient.medicalPractitionerId)?.fullName;
    };

    const getOutOfContractClass = (stage: ComponentFunctions.internalStage, isRealized: boolean): string => {
        if (stage.stageId) {
            return '';
        }

        // ni una visita extra ni una nota son anomalías: tienen su propio marcador,
        // no el tinte de fuera de contrato
        if (stage.isExtraProtocolVisit || stage.isNoteVisit) {
            return '';
        }

        return isRealized ? ' warning-realized' : ' warning';
    };

    const getStatusBadgeCode = (statusDescription: string): string => {
        const normalized = statusDescription
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .trim();

        if (!normalized) {
            return '';
        }

        // Explicitly separate screening from screen failure.
        if (normalized.includes('screen fail') || normalized.includes('screen faill')) {
            return 'SF';
        }

        if (normalized.includes('screen')) {
            return 'SC';
        }

        const words = normalized.split(/\s+/).filter(Boolean);

        if (words.length >= 2) {
            return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
        }

        return normalized.slice(0, 2).toUpperCase();
    };

    const isBranchAssignmentActive = (branch: ProtocolPatientContractBranch, referenceDate: Date = new Date()): boolean => {
        const from = branch.dateFrom ? new Date(branch.dateFrom) : undefined;
        const to = branch.dateTo ? new Date(branch.dateTo) : undefined;

        if (from && from.getTime() > referenceDate.getTime()) {
            return false;
        }

        if (to && to.getTime() < referenceDate.getTime()) {
            return false;
        }

        return true;
    };

    const getPatientBranchHistory = (patientNumberInProtocol: string): ProtocolPatientContractBranch[] => {
        const normalizedPatientNumber = patientNumberInProtocol?.trim().toLowerCase();
        return props.ProtocolPatientContractBranches
            .filter((branch) => branch.numberInProtocol?.trim().toLowerCase() === normalizedPatientNumber)
            .sort((a, b) => new Date(a.dateFrom).getTime() - new Date(b.dateFrom).getTime());
    };

    const getAssignedBranchByPatientNumber = (patientNumberInProtocol: string): ProtocolPatientContractBranch | undefined => {
        const history = getPatientBranchHistory(patientNumberInProtocol);

        if (history.length === 0) {
            return undefined;
        }

        const now = new Date();
        // Exclude assignments that haven't started yet (future dateFrom) so a scheduled/future
        // branch is never shown as "current" ahead of the one actually active today.
        const startedAssignments = history.filter((branch) => new Date(branch.dateFrom).getTime() <= now.getTime());

        if (startedAssignments.length === 0) {
            return undefined;
        }

        // among started assignments, prefer the most recently started one that's currently active
        const activeAssignments = startedAssignments.filter((branch) => isBranchAssignmentActive(branch, now));
        return activeAssignments.at(-1) || startedAssignments.at(-1);
    };

    // Each assignment gets an equal-width segment (1/N) regardless of its real date span, ordered
    // oldest→newest. Cutoffs mark the boundary between consecutive segments. One Gantt row is
    // rendered per UNIQUE branch, showing a bar in every segment where that branch was assigned
    // (gray for past periods, highlighted for the currently active one) and leaving other segments blank
    // — so returning to a previously used branch shows a gap followed by a new bar on that same row.
    const getBranchGanttTimeline = (history: ProtocolPatientContractBranch[]) => {
        const segmentCount = Math.max(history.length, 1);
        const segmentWidthPercent = 100 / segmentCount;

        const cutoffs = history.slice(1).map((item) => new Date(item.dateFrom))
            .map((date, index) => ({ percent: (index + 1) * segmentWidthPercent, date }));

        const uniqueBranches = history.reduce((acc, item) => {
            if (!acc.some((branch) => branch.contractBranchId === item.contractBranchId)) {
                acc.push({ contractBranchId: item.contractBranchId, contractBranchName: item.contractBranchName });
            }
            return acc;
        }, [] as { contractBranchId: number; contractBranchName: string }[]);

        return { segmentWidthPercent, cutoffs, uniqueBranches };
    };

    const getContractBranchNameById = (branchId?: number | string): string | undefined => {
        if (!branchId) {
            return undefined;
        }

        return contractBranches.find((branch) => branch.id?.toString() === branchId.toString())?.branchName;
    };

    const getPatientBranchId = (patientNumberInProtocol: string): number | undefined => {
        const normalizedPatientNumber = patientNumberInProtocol?.trim().toLowerCase();

        const protocolPatient = props.ProtocolPatients.find(
            (item) => item.numberInProtocol?.trim().toLowerCase() === normalizedPatientNumber
        );

        if (protocolPatient?.contractBranchId) {
            return protocolPatient.contractBranchId;
        }

        return getAssignedBranchByPatientNumber(patientNumberInProtocol)?.contractBranchId;
    };

    const buildGridHeaderModel = (sourceVisits: DisplayStageColumn[]) => {
        const stageAssociationCount = sourceVisits.reduce((acc, stage) => {
            if (stage.stageId) {
                acc.set(stage.stageId, (acc.get(stage.stageId) || 0) + 1);
            }
            return acc;
        }, new Map<number, number>());

        const groups: {
            groupKey: string;
            // unique per rendered group: groupKey repeats if a stage ever gets split in two runs
            groupId: string;
            title: string;
            isOutOfContract: boolean;
            isExtraProtocolVisit: boolean;
            isNoteVisit: boolean;
            displayColumns: DisplayStageColumn[];
            headerColSpan: number;
            stageLinksCount: number;
        }[] = [];

        sourceVisits.forEach((stage) => {
            const groupKey = stage.stageId
                ? `stage-${stage.stageId}`
                : `${stage.isExtraProtocolVisit ? 'extra' : stage.isNoteVisit ? 'note' : 'out'}-${stage.columnKey}`;
            const title = stage.stageId || stage.isExtraProtocolVisit || stage.isNoteVisit
                ? stage.stageName
                : t('protocolActivity.table.outOfContractGroup', { defaultValue: 'Fuera de contrato' });

            const lastGroup = groups.at(-1);

            if (lastGroup?.groupKey === groupKey) {
                lastGroup.displayColumns.push(stage);
                lastGroup.headerColSpan = lastGroup.displayColumns.length;
                return;
            }

            groups.push({
                groupKey,
                groupId: `${groupKey}--${stage.columnKey}`,
                title,
                isOutOfContract: ComponentFunctions.IsOutOfContractStage(stage),
                isExtraProtocolVisit: !!stage.isExtraProtocolVisit,
                isNoteVisit: !!stage.isNoteVisit,
                displayColumns: [stage],
                headerColSpan: 1,
                stageLinksCount: stage.stageId ? (stageAssociationCount.get(stage.stageId) || 0) : 0
            });
        });

        const displayColumns = groups.flatMap((group) => group.displayColumns);
        const groupEndColumnKeys = new Set(
            groups
                .map((group) => group.displayColumns.at(-1)?.columnKey)
                .filter((columnKey): columnKey is string => !!columnKey)
        );

        return { groups, displayColumns, groupEndColumnKeys };
    };

    // The extra-protocol and note columns are built from the whole protocol, but each grid shows a
    // subset of patients: a column nobody in this grid had would be an empty column, so it is dropped.
    const withUsedExtraProtocolColumns = useCallback((
        columns: ComponentFunctions.internalStage[],
        gridPatients: string[]
    ): ComponentFunctions.internalStage[] => {
        const gridPatientNumbers = new Set(gridPatients.map((patient) => patient?.trim().toLowerCase()));

        return columns.filter((column) => (!column.isExtraProtocolVisit && !column.isNoteVisit) || patientVisits.some((visit) =>
            gridPatientNumbers.has(visit.numberInProtocol?.trim().toLowerCase())
            && ComponentFunctions.MatchesStage(visit, column)
        ));
    }, [patientVisits]);

    const branchGridModels = useMemo(() => {
        const stageBranchIds = Array.from(new Set(
            visits
                .filter((stage) => stage.stageId && stage.branchId !== undefined && stage.branchId !== null && !stage.isOrphanBranch)
                .map((stage) => Number(stage.branchId))
                .filter((branchId) => Number.isFinite(branchId))
        ));

        const patientBranchIds = Array.from(new Set(
            currentPatiens
                .map((patientNumberInProtocol) => getPatientBranchId(patientNumberInProtocol))
                .filter((branchId): branchId is number => branchId !== undefined && branchId !== null)
                .map((branchId) => Number(branchId))
                .filter((branchId) => Number.isFinite(branchId) && !isOrphanBranchId(branchId))
        ));

        const contractBranchIds = Array.from(new Set(
            (contractBranches || [])
                .map((branch) => Number(branch.id))
                .filter((id) => Number.isFinite(id))
        ));

        const allBranchIds = Array.from(new Set([...stageBranchIds, ...patientBranchIds, ...contractBranchIds]))
            .sort((a, b) => a - b);

        const unassignedVisits = visits.filter((stage) =>
            !stage.stageId ||
            stage.branchId === undefined ||
            stage.branchId === null ||
            stage.isOrphanBranch
        );

        const unassignedPatients = currentPatiens.filter((patientNumberInProtocol) => {
            const patientBranchId = getPatientBranchId(patientNumberInProtocol);
            return patientBranchId === undefined || patientBranchId === null || isOrphanBranchId(patientBranchId);
        });

        if (allBranchIds.length === 0) {
            return [{
                key: 'branch-unassigned',
                title: t('protocolActivity.table.unassignedBranch', { defaultValue: 'Sin rama' }),
                patients: currentPatiens,
                visits: withUsedExtraProtocolColumns(visits, currentPatiens)
            }];
        }

        const branchSpecificGrids = allBranchIds.map((branchId) => {
            const scopedVisits = visits.filter((stage) =>
                stage.isOrphanBranch ? false :
                !stage.stageId ||
                stage.branchId === undefined ||
                stage.branchId === null ||
                Number(stage.branchId) === branchId
            );

            const scopedPatients = currentPatiens.filter((patientNumberInProtocol) => {
                const patientBranchId = getPatientBranchId(patientNumberInProtocol);
                return patientBranchId === branchId;
            });

            return {
                key: `branch-${branchId}`,
                title: getContractBranchNameById(branchId) || t('protocolActivity.table.branchFallback', { branchId, defaultValue: 'Rama {{branchId}}' }),
                patients: scopedPatients,
                visits: withUsedExtraProtocolColumns(scopedVisits, scopedPatients)
            };
        });

        const baseGrid = {
            key: 'branch-unassigned',
            title: t('protocolActivity.table.unassignedBranch', { defaultValue: 'Sin rama' }),
            patients: unassignedPatients,
            visits: withUsedExtraProtocolColumns(unassignedVisits, unassignedPatients)
        };

        // The "Sin rama" tab also has to appear when every patient has a branch but some stages
        // point at a deleted one, otherwise those visits would not be reachable from any tab.
        if (unassignedPatients.length > 0 || unassignedVisits.some((stage) => stage.isOrphanBranch)) {
            const combinedGrids = [baseGrid, ...branchSpecificGrids];
            return combinedGrids.filter((grid, index, array) =>
                index === array.findIndex((candidate) => candidate.key === grid.key)
            );
        }

        return branchSpecificGrids.filter((grid, index, array) =>
            index === array.findIndex((candidate) => candidate.key === grid.key)
        );
    }, [visits, currentPatiens, props.ProtocolPatients, props.ProtocolPatientContractBranches, t, contractBranches, isOrphanBranchId, withUsedExtraProtocolColumns]);

    // Every visit performed in the protocol, whatever branch or tab it belongs to: the summary is a
    // total, not a reading of the grid on screen. Las notas se descuentan acá: se ven en la grilla
    // pero no son actividad, y sumarlas al total desdibujaría el dato. Con eso las partes vuelven a
    // cerrar exacto: cada visita se cuenta una sola vez, por su estado o como extra del protocolo.
    const visitStats = useMemo(() => {
        const countByStatus = new Map<number, number>();
        let extraProtocolCount = 0;

        const countableVisits = ComponentFunctions.WithoutNoteVisits(patientVisits);

        countableVisits.forEach((visit) => {
            if (ComponentFunctions.IsExtraProtocolPatientVisit(visit)) {
                extraProtocolCount += 1;

                return;
            }

            countByStatus.set(visit.status, (countByStatus.get(visit.status) || 0) + 1);
        });

        return {
            total: countableVisits.length,
            extraProtocolCount,
            byStatus: VISIT_STATUS_SUMMARY
                .map((item) => ({
                    ...item,
                    label: ComponentFunctions.GetVisitStatusDescription(item.status),
                    count: countByStatus.get(item.status) || 0
                }))
                .filter((item) => item.count > 0)
        };
    }, [patientVisits]);

    const hasDefinedBranchTabs = useMemo(
        () => branchGridModels.some((gridModel) => gridModel.key !== 'branch-unassigned'),
        [branchGridModels]
    );

    useEffect(() => {
        if (branchGridModels.length === 0) {
            setActiveBranchGridKey('');
            return;
        }

        if (!activeBranchGridKey || !branchGridModels.some((gridModel) => gridModel.key === activeBranchGridKey)) {
            setActiveBranchGridKey(branchGridModels[0].key);
        }
    }, [branchGridModels, activeBranchGridKey]);

    const onBranchTabsKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
            return;
        }

        event.preventDefault();

        const lastIndex = branchGridModels.length - 1;
        const currentIndex = branchGridModels.findIndex((gridModel) => gridModel.key === activeBranchGridKey);
        const nextIndex = event.key === 'Home' ? 0
            : event.key === 'End' ? lastIndex
                : event.key === 'ArrowDown' ? (currentIndex >= lastIndex ? 0 : currentIndex + 1)
                    : (currentIndex <= 0 ? lastIndex : currentIndex - 1);
        const nextKey = branchGridModels[nextIndex]?.key;

        if (!nextKey) {
            return;
        }

        setActiveBranchGridKey(nextKey);
        branchTabsRef.current?.querySelector<HTMLButtonElement>(`[data-tab-key="${nextKey}"]`)?.focus();
    };

    const clearPatientBranchEditingState = (patientNumberInProtocol: string) => {
        setEditingBranchAssignment((prev) => (prev?.patient === patientNumberInProtocol ? null : prev));
        setSelectedPatientBranchMap((prev) => {
            const next = { ...prev };
            delete next[patientNumberInProtocol];
            return next;
        });
        setPatientBranchDateRange((prev) => {
            const next = { ...prev };
            delete next[patientNumberInProtocol];
            return next;
        });
    };

    const onEditPatientBranch = (patientNumberInProtocol: string, branchItem: ProtocolPatientContractBranch) => {
        setEditingBranchAssignment({ patient: patientNumberInProtocol, id: branchItem.id });
        setSelectedPatientBranchMap((prev) => ({
            ...prev,
            [patientNumberInProtocol]: [{ id: branchItem.contractBranchId, branchName: branchItem.contractBranchName } as ContractBranch]
        }));
        setPatientBranchDateRange((prev) => ({
            ...prev,
            [patientNumberInProtocol]: {
                dateFrom: new Date(branchItem.dateFrom),
                dateTo: branchItem.dateTo ? new Date(branchItem.dateTo) : undefined
            }
        }));
    };

    const onSavePatientBranch = async (patientNumberInProtocol: string) => {
        const selectedBranch = selectedPatientBranchMap[patientNumberInProtocol]?.[0];
        const dateRange = patientBranchDateRange[patientNumberInProtocol];
        const editingId = editingBranchAssignment?.patient === patientNumberInProtocol ? editingBranchAssignment.id : undefined;

        if (!selectedBranch?.id || !selectedBranch.branchName || !dateRange?.dateFrom) {
            return;
        }

        setSavingBranchPatient(patientNumberInProtocol);
        setBranchSaveMessage(null);

        try {
            props.SetProtocolPatientContractBranch(
                props.ProtocolId,
                patientNumberInProtocol,
                selectedBranch.id,
                selectedBranch.branchName,
                dateRange.dateFrom,
                dateRange.dateTo,
                editingId
            );

            setBranchSaveMessage({
                type: 'success',
                text: editingId
                    ? `Asignación actualizada para paciente ${patientNumberInProtocol}: ${selectedBranch.branchName}`
                    : `Rama asignada a paciente ${patientNumberInProtocol}: ${selectedBranch.branchName}`
            });
            setBranchPopoverPatient(null);
            clearPatientBranchEditingState(patientNumberInProtocol);
            setTimeout(() => setBranchSaveMessage(null), 3000);
        } catch (error) {
            setBranchSaveMessage({
                type: 'error',
                text: 'Error al guardar la rama del paciente'
            });
        } finally {
            setSavingBranchPatient(null);
        }
    };

    const onRemovePatientBranch = async (patientNumberInProtocol: string, contractBranchAssociationId: string) => {
        setSavingBranchPatient(patientNumberInProtocol);
        setBranchSaveMessage(null);

        try {
            props.DeleteProtocolPatientContractBranch(props.ProtocolId, contractBranchAssociationId, patientNumberInProtocol);

            await new Promise(resolve => setTimeout(resolve, 800));

            props.GetProtocolPatientContractBranches(props.ProtocolId);

            setBranchSaveMessage({
                type: 'success',
                text: `Rama eliminada para paciente ${patientNumberInProtocol}`
            });
            setTimeout(() => setBranchSaveMessage(null), 3000);
        } catch (error) {
            setBranchSaveMessage({
                type: 'error',
                text: 'Error al eliminar la rama del paciente'
            });
        } finally {
            setSavingBranchPatient(null);
        }
    };

    const startPatientColumnResize = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();

        const startX = event.clientX;
        const initialWidth = patientColumnWidth;

        setIsResizingPatientColumn(true);

        const onMouseMove = (moveEvent: MouseEvent) => {
            setPatientColumnWidth(clampPatientColumnWidth(initialWidth + (moveEvent.clientX - startX)));
        };

        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            setIsResizingPatientColumn(false);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    }, [patientColumnWidth]);

    // a separator is focusable, so it is resized with the keyboard too
    const onPatientColumnResizeKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        const step = event.shiftKey ? 32 : 8;

        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            setPatientColumnWidth((current) => clampPatientColumnWidth(current + (event.key === 'ArrowLeft' ? -step : step)));

            return;
        }

        if (event.key === 'Home') {
            event.preventDefault();
            setPatientColumnWidth(PATIENT_COLUMN_DEFAULT_WIDTH);
        }
    }, []);

    const tableStyle = useMemo(() => ({
        ['--patient-col-width' as any]: `${patientColumnWidth}px`
    } as React.CSSProperties), [patientColumnWidth]);


    const gridOptionButtons = (
        <>
            <Tooltip
                content={hideExtraProtocolVisits
                    ? t('protocolActivity.toolbar.showExtraProtocolVisits', { defaultValue: 'Mostrar las visitas no programadas y las notas' })
                    : t('protocolActivity.toolbar.compareGrid', { defaultValue: 'Comparar: ocultar las visitas no programadas y las notas' })}
                relationship='label'
                withArrow
            >
                <Button
                    icon={hideExtraProtocolVisits ? <ColumnTriple20Regular /> : <Column20Regular />}
                    appearance={hideExtraProtocolVisits ? 'primary' : 'secondary'}
                    aria-pressed={hideExtraProtocolVisits}
                    onClick={() => setHideExtraProtocolVisits((current) => !current)}
                />
            </Tooltip>
            <Tooltip
                content={isMaximized
                    ? t('protocolActivity.toolbar.restoreGrid', { defaultValue: 'Restaurar el tamaño de la grilla (Esc)' })
                    : t('protocolActivity.toolbar.maximizeGrid', { defaultValue: 'Maximizar la grilla' })}
                relationship='label'
                withArrow
            >
                <Button
                    icon={isMaximized ? <FullScreenMinimize20Regular /> : <FullScreenMaximize20Regular />}
                    appearance={isMaximized ? 'primary' : 'secondary'}
                    aria-pressed={isMaximized}
                    onClick={() => setIsMaximized((current) => !current)}
                />
            </Tooltip>
        </>
    );

    const optionsToolbar = (
        <div className='visitButton'>
            {props.Contract?.id && <Button icon={<CalendarSettingsRegular />} onClick={openPanel}>Verificar asociación de visitas</Button>}
            <Tooltip content="Actualizar datos" relationship="label" withArrow>
                <Button
                    icon={<ArrowSync16Regular className={isRefreshing ? 'refreshIconSpin' : ''} />}
                    onClick={ResetInfo}
                    aria-label="Actualizar"
                    disabled={isRefreshing}
                />
            </Tooltip>
            {gridOptionButtons}
            <div className='pa-visit-stats' role='group' aria-label={t('protocolActivity.stats.aria', { defaultValue: 'Resumen de visitas realizadas' })}>
                <span
                    className='pa-visit-stats__total'
                    title={t('protocolActivity.stats.total', { defaultValue: 'Total de visitas realizadas en el protocolo' })}
                >
                    <CalendarMultiple16Regular aria-hidden='true' />
                    <strong>{visitStats.total}</strong>
                    <span className='pa-visit-stats__total-label'>
                        {t('protocolActivity.stats.visits', { defaultValue: 'visitas' })}
                    </span>
                </span>
                {visitStats.byStatus.length > 0 && <span className='pa-visit-stats__divider' aria-hidden='true' />}
                {visitStats.byStatus.map((item) => (
                    <span
                        key={`visit-stat-${item.status}`}
                        className='pa-visit-stats__item'
                        title={`${item.label}: ${item.count}`}
                    >
                        <span className={`pa-legend__swatch pa-legend__swatch--${item.swatch}`} aria-hidden='true' />
                        {item.count}
                    </span>
                ))}
                {visitStats.extraProtocolCount > 0 && (
                    <>
                        <span className='pa-visit-stats__divider' aria-hidden='true' />
                        <span
                            className='pa-visit-stats__item'
                            title={t('protocolActivity.stats.extraProtocol', {
                                count: visitStats.extraProtocolCount,
                                defaultValue: '{{count}} visitas no programadas (no se cuentan por estado)'
                            })}
                        >
                            <span className='pa-legend__swatch pa-legend__swatch--extra' aria-hidden='true' />
                            {visitStats.extraProtocolCount}
                        </span>
                    </>
                )}
            </div>
        </div>
    );

    const activityLegend = (
        <div className='pa-legend' aria-label='Leyenda de estados de visita'>
            <span className='pa-legend__title'>Estados</span>
            <span className='pa-legend__item'><span className='pa-legend__swatch pa-legend__swatch--completed' />Completada</span>
            <span className='pa-legend__item'><span className='pa-legend__swatch pa-legend__swatch--autocompleted' />Autocompletada</span>
            <span className='pa-legend__item'><span className='pa-legend__swatch pa-legend__swatch--started' />Iniciada</span>
            <span className='pa-legend__item'><span className='pa-legend__swatch pa-legend__swatch--changed' />Modificada</span>
            <span className='pa-legend__item'><span className='pa-legend__swatch pa-legend__swatch--skipped' />Salteada</span>
            <span className='pa-legend__item'><span className='pa-legend__swatch pa-legend__swatch--failed' />Falla</span>
            <span className='pa-legend__item'><span className='pa-legend__swatch pa-legend__swatch--warning' />Fuera de contrato</span>
            <span className='pa-legend__item'><span className='pa-legend__swatch pa-legend__swatch--warningRealized' />Fuera de contrato (realizada)</span>
            <span className='pa-legend__item'><span className='pa-legend__swatch pa-legend__swatch--extra' />Visita no programada</span>
            <span className='pa-legend__item'><span className='pa-legend__swatch pa-legend__swatch--note' />Anotación (no es una visita)</span>
        </div>
    );

    const Form = (
        <StageFixFormComponent
            isOpen={isOpen}
            openPanel={openPanel}
            dismissPanel={dismissPanel}
            ProtocolId={props.ProtocolId}
        />
    );

    return (
        <>
            {branchSaveMessage && (
                <MessageBar intent={branchSaveMessage.type === 'success' ? 'success' : 'error'}>
                    <MessageBarBody>
                        {branchSaveMessage.text}
                    </MessageBarBody>
                </MessageBar>
            )}
            {props.Patients.length === 0 && <MessageBar>
                <MessageBarBody>
                    <MessageBarTitle>No hay pacientes registrados</MessageBarTitle>
                    No se han econtrado pacientes registrados para este protocolo.{" "}
                </MessageBarBody>
            </MessageBar>}
            {props.ContractId === null && <MessageBar>
                <MessageBarBody>
                    <MessageBarTitle>No hay contrato asociado</MessageBarTitle>
                    No se han asociado un contrato a este protocolo.{" "}
                </MessageBarBody>
            </MessageBar>}
            {!isMaximized && optionsToolbar}
            {!isMaximized && visits?.length > 0 && activityLegend}
            {visits?.length > 0 && (
            <>
            <div className={`pa-grid-shell ${isMaximizedShellVisible ? 'pa-grid-shell--maximized' : ''} ${isMaximizedShellClosing ? 'is-closing' : ''}`}>
            {isMaximizedShellVisible && (
                <div className='pa-grid-shell__toolbar'>
                    {optionsToolbar}
                    {activityLegend}
                </div>
            )}
            <div className={`pa-branch-layout ${!hasDefinedBranchTabs ? 'pa-branch-layout--single' : ''}`}>
            {hasDefinedBranchTabs && (
                <div
                    ref={branchTabsRef}
                    className='pa-branch-tabs'
                    role='tablist'
                    aria-orientation='vertical'
                    aria-label={t('protocolActivity.table.branchTabsAria', { defaultValue: 'Ramas' })}
                    onKeyDown={onBranchTabsKeyDown}
                >
                    <div className='pa-branch-tabs__title'>
                        <span>{t('protocolActivity.table.branchTabsAria', { defaultValue: 'Ramas' })}</span>
                        <span className='pa-branch-tabs__title-count'>{branchGridModels.length}</span>
                    </div>
                    {branchGridModels.map((gridModel) => {
                        const isActive = gridModel.key === activeBranchGridKey;
                        const isUnassigned = gridModel.key === 'branch-unassigned';

                        return (
                            <button
                                key={`tab-${gridModel.key}`}
                                type='button'
                                role='tab'
                                id={`branch-tab-${gridModel.key}`}
                                data-tab-key={gridModel.key}
                                aria-selected={isActive}
                                aria-controls={`branch-panel-${gridModel.key}`}
                                // roving tabindex: the rail is one stop, the arrows move inside it
                                tabIndex={isActive ? 0 : -1}
                                title={gridModel.title}
                                className={`pa-branch-tabs__item ${isActive ? 'is-active' : ''} ${gridModel.patients.length === 0 ? 'is-empty' : ''}`}
                                onClick={() => setActiveBranchGridKey(gridModel.key)}
                            >
                                {isUnassigned
                                    ? <SubtractCircle16Regular className='pa-branch-tabs__icon' aria-hidden='true' />
                                    : <Branch16Regular className='pa-branch-tabs__icon' aria-hidden='true' />}
                                <span className='pa-branch-tabs__name'>{gridModel.title}</span>
                                <span className='pa-branch-tabs__count'>{gridModel.patients.length}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            <div className='pa-branch-layout__content'>
            {branchGridModels
                .filter((gridModel) => !hasDefinedBranchTabs || gridModel.key === activeBranchGridKey)
                .map((gridModel) => {
                const { groups: stageColumnGroupsForRender, displayColumns: displayStageColumns, groupEndColumnKeys } = buildGridHeaderModel(gridModel.visits);

                return (
            <div
                className='tableContainer pa-branch-grid'
                key={gridModel.key}
                id={`branch-panel-${gridModel.key}`}
                role={hasDefinedBranchTabs ? 'tabpanel' : undefined}
                aria-labelledby={hasDefinedBranchTabs ? `branch-tab-${gridModel.key}` : undefined}
            >
                <div className='pa-branch-grid__header'>
                    <span className='pa-branch-grid__accent' aria-hidden='true' />
                    <div className='pa-branch-grid__heading'>
                        <span className='pa-branch-grid__eyebrow'>
                            {t('protocolActivity.table.branchGridTitle', { defaultValue: 'Rama' })}
                        </span>
                        <span className='pa-branch-grid__title' title={gridModel.title}>{gridModel.title}</span>
                    </div>
                    <div className='pa-branch-grid__meta'>
                        <span
                            className='pa-branch-grid__count'
                            title={t('protocolActivity.table.branchPatientsCount', { defaultValue: '{{count}} pacientes', count: gridModel.patients.length })}
                        >
                            <People16Regular aria-hidden='true' />
                            {gridModel.patients.length}
                        </span>
                        <span
                            className='pa-branch-grid__count'
                            title={t('protocolActivity.table.branchVisitsCount', { defaultValue: '{{count}} visitas en pantalla', count: displayStageColumns.length })}
                        >
                            <CalendarMultiple16Regular aria-hidden='true' />
                            {displayStageColumns.length}
                        </span>
                    </div>
                </div>
                <table
                    className={`table-header-rotated ${areExtraColumnsCollapsed ? 'table-header-rotated--extra-collapsed' : ''}`}
                    style={tableStyle}
                >
                    <thead>
                        <tr >
                            <th className='pa-patient-header-cell'>
                                <div className='pa-patient-header-content'>
                                    <People16Regular className='pa-patient-header-icon' aria-hidden='true' />
                                    <span className='pa-patient-header-label'>
                                        {t('protocolActivity.table.patientsColumn', { defaultValue: 'Pacientes' })}
                                    </span>
                                    <span className='pa-patient-header-count'>{gridModel.patients.length}</span>
                                </div>
                                <div
                                    className={`pa-column-resizer ${isResizingPatientColumn ? 'is-dragging' : ''}`}
                                    role='separator'
                                    aria-orientation='vertical'
                                    aria-label={t('protocolActivity.table.resizePatientsColumn', { defaultValue: 'Redimensionar columna de pacientes' })}
                                    aria-valuenow={patientColumnWidth}
                                    aria-valuemin={PATIENT_COLUMN_MIN_WIDTH}
                                    aria-valuemax={PATIENT_COLUMN_MAX_WIDTH}
                                    tabIndex={0}
                                    title={t('protocolActivity.table.resizePatientsColumnHint', { defaultValue: 'Arrastrar para redimensionar · doble clic para restablecer' })}
                                    onMouseDown={startPatientColumnResize}
                                    onDoubleClick={() => setPatientColumnWidth(PATIENT_COLUMN_DEFAULT_WIDTH)}
                                    onKeyDown={onPatientColumnResizeKeyDown}
                                >
                                    <span className='pa-column-resizer__grip' aria-hidden='true'>
                                        <span />
                                        <span />
                                        <span />
                                    </span>
                                </div>
                            </th>
                            {stageColumnGroupsForRender.map((group) => {
                                const isMultiVisitStage = group.stageLinksCount > 1;
                                const hasBranchAssociation = group.displayColumns.some(
                                    (stageColumn) => stageColumn.branchId !== undefined && stageColumn.branchId !== null && !stageColumn.isOrphanBranch
                                );
                                const orphanBranchIds = Array.from(new Set(
                                    group.displayColumns
                                        .filter((stageColumn) => stageColumn.isOrphanBranch)
                                        .map((stageColumn) => stageColumn.orphanBranchId ?? Number(stageColumn.branchId))
                                ));
                                const hasOrphanBranch = orphanBranchIds.length > 0;
                                const orphanBranchTooltip = t('protocolActivity.table.orphanBranchTooltip', {
                                    branchIds: orphanBranchIds.join(', '),
                                    defaultValue: 'La rama asociada (#{{branchIds}}) fue eliminada del contrato'
                                });
                                const groupDisplayName = group.isOutOfContract
                                    ? t('protocolActivity.table.outOfContractShort', { defaultValue: 'N/E' })
                                    : group.title;
                                const groupTitleText = group.isOutOfContract
                                    ? t('protocolActivity.table.outOfContractTitle', { defaultValue: 'No existe en el contrato' })
                                    : group.isExtraProtocolVisit
                                        ? t('protocolActivity.table.extraProtocolVisitTitle', { defaultValue: 'Visita no programada (fuera del flowchart)' })
                                        : group.title;

                                return (
                                    <th
                                        key={`group-${group.groupId}`}
                                        colSpan={group.headerColSpan}
                                        className={`pa-stage-group-header ${group.isExtraProtocolVisit ? 'pa-stage-group-header--extra' : ''} ${group.isNoteVisit ? 'pa-stage-group-header--note' : ''}`}
                                    >
                                        <div className='pa-stage-group-header__content'>
                                            <div
                                                id={`stage-header-${gridModel.key}-${group.groupId}`}
                                                className='pa-stage-header-trigger'
                                                title={groupTitleText}
                                                role='button'
                                                tabIndex={0}
                                                onClick={() => {
                                                    const targetId = `stage-header-${gridModel.key}-${group.groupId}`;
                                                    const relations = group.displayColumns.map((stageColumn) =>
                                                        `${groupDisplayName} -> ${stageColumn.flowchartName || t('protocolActivity.table.noFlowchartVisit', { defaultValue: 'Sin visita asociada' })}`
                                                    );

                                                    setStageHeaderCallout((current) => {
                                                        if (current?.targetId === targetId) {
                                                            return null;
                                                        }

                                                        return {
                                                            targetId,
                                                            stageTitle: groupDisplayName,
                                                            stageDescription: groupTitleText,
                                                            relations
                                                        };
                                                    });
                                                }}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                        event.preventDefault();
                                                        const targetId = `stage-header-${gridModel.key}-${group.groupId}`;
                                                        const relations = group.displayColumns.map((stageColumn) =>
                                                            `${groupDisplayName} -> ${stageColumn.flowchartName || t('protocolActivity.table.noFlowchartVisit', { defaultValue: 'Sin visita asociada' })}`
                                                        );

                                                        setStageHeaderCallout((current) => {
                                                            if (current?.targetId === targetId) {
                                                                return null;
                                                            }

                                                            return {
                                                                targetId,
                                                                stageTitle: groupDisplayName,
                                                                stageDescription: groupTitleText,
                                                                relations
                                                            };
                                                        });
                                                    }
                                                }}
                                            >
                                                <span className='pa-stage-group-header__title-wrap'>
                                                    <span className='pa-stage-group-header__title'>{groupDisplayName}</span>
                                                </span>
                                            </div>
                                            <div className='pa-stage-group-header__flags'>
                                                {hasBranchAssociation && (
                                                    <span
                                                        className='pa-column-flag pa-column-flag--branch'
                                                        title={t('protocolActivity.table.stageHasBranch', { defaultValue: 'Etapa asociada a rama' })}
                                                    >
                                                        <Branch16Regular />
                                                    </span>
                                                )}
                                                {hasOrphanBranch && (
                                                    <span className='pa-column-flag pa-column-flag--orphan' title={orphanBranchTooltip}>
                                                        <Warning16Filled />
                                                    </span>
                                                )}
                                                {group.isExtraProtocolVisit && (
                                                    <span
                                                        className='pa-column-flag pa-column-flag--extra'
                                                        title={t('protocolActivity.table.extraProtocolVisitFlag', { defaultValue: 'Visita no programada' })}
                                                    >
                                                        <CalendarAdd16Regular />
                                                    </span>
                                                )}
                                                {group.isNoteVisit && (
                                                    <span
                                                        className='pa-column-flag pa-column-flag--note'
                                                        title={t('protocolActivity.table.noteFlag', { defaultValue: 'Anotación, no es una visita' })}
                                                    >
                                                        <Note16Regular />
                                                    </span>
                                                )}
                                                {isMultiVisitStage && (
                                                    <span
                                                        className='pa-column-flag pa-column-flag--stage'
                                                        title={t('protocolActivity.table.multiVisitStage', { defaultValue: 'Etapa con más de una visita asociada' })}
                                                    >
                                                        <LinkMultiple16Regular />
                                                    </span>
                                                )}
                                                {group.displayColumns.length > 1 && (
                                                    <span
                                                        className='pa-column-flag pa-column-flag--count'
                                                        title={t('protocolActivity.table.visitCountInStage', { count: group.displayColumns.length, defaultValue: '{{count}} visitas' })}
                                                    >
                                                        {group.displayColumns.length}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {gridModel.patients?.map((patient: string) => {
                            const normalizedPatientNumber = patient?.trim().toLowerCase();
                            const protocolPatient = props.ProtocolPatients.find(
                                (p) => p.numberInProtocol?.trim().toLowerCase() === normalizedPatientNumber
                            );
                            const assignedBranch = getAssignedBranchByPatientNumber(patient);
                            const displayedBranchId = protocolPatient?.contractBranchId || assignedBranch?.contractBranchId;
                            const displayedBranchName =
                                protocolPatient?.contractBranchName ||
                                assignedBranch?.contractBranchName ||
                                getContractBranchNameById(displayedBranchId);
                            const selectedBranchForPicker =
                                displayedBranchId && displayedBranchName
                                    ? ([{ id: Number(displayedBranchId), branchName: displayedBranchName } as ContractBranch])
                                    : [];
                            const referrerName = getReferrerNameByPatient(patient);
                            const patientHistory = getPatientBranchHistory(patient);
                            const branchTimeline = getBranchGanttTimeline(patientHistory);
                            return (
                            <tr key={`patient-row-${patient}`}>
                                <th style={{ borderBottom: 'solid 1px lightgray' }}>
                                    <Popover
                                        open={branchPopoverPatient === patient}
                                        onOpenChange={(_, data) => {
                                            if (data.open) {
                                                setBranchPopoverPatient(patient);
                                                setPatientBranchDateRange((prev) => prev[patient] ? prev : ({
                                                    ...prev,
                                                    [patient]: { dateFrom: new Date(), dateTo: undefined }
                                                }));
                                            } else {
                                                setBranchPopoverPatient(null);
                                                clearPatientBranchEditingState(patient);
                                            }
                                        }}
                                        positioning='below-start'
                                        withArrow
                                    >
                                        <PopoverTrigger disableButtonEnhancement>
                                            <button type='button' className='pa-patient-trigger'>
                                                <span className='pa-patient-number' title={protocolPatient?.statusDescription}>{patient}</span>
                                                <div className='pa-patient-badges'>
                                                    <Tooltip relationship='label' withArrow content={t('protocolActivity.branchAssignment.branchBadgeTooltip')}>
                                                        <span className="pa-patient-badge pa-patient-badge--branch-action">
                                                            <Branch16Regular />
                                                        </span>
                                                    </Tooltip>
                                                    {protocolPatient?.statusDescription && (
                                                        <Tooltip relationship='label' withArrow content={protocolPatient.statusDescription}>
                                                            <span className="pa-patient-badge pa-patient-badge--status">
                                                                {getStatusBadgeCode(protocolPatient.statusDescription)}
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                    {displayedBranchName && (
                                                        <Tooltip relationship='label' withArrow content={`${t('protocolActivity.branchAssignment.branchBadgeTooltip')}: ${displayedBranchName}`}>
                                                            <span className="pa-patient-badge pa-patient-badge--branch">
                                                                {displayedBranchName}
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                    {referrerName && (
                                                        <Tooltip relationship='label' withArrow content={`Derivado por: ${referrerName}`}>
                                                            <span className="pa-patient-badge pa-patient-badge--referral">D</span>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </button>
                                        </PopoverTrigger>
                                        <PopoverSurface tabIndex={-1}>
                                            <div className='pa-branch-popover'>
                                                <Text weight='semibold'>
                                                    {t('protocolActivity.branchAssignment.title', { patient })}
                                                </Text>

                                                <div className='pa-branch-popover__history'>
                                                    <Caption1Stronger>{t('protocolActivity.branchAssignment.historyTitle')}</Caption1Stronger>
                                                    {patientHistory.length === 0 ? (
                                                        <Caption2>{t('protocolActivity.branchAssignment.noHistory')}</Caption2>
                                                    ) : (
                                                        <div className='pa-branch-gantt'>
                                                            {branchTimeline.cutoffs.length > 0 && (
                                                                <div className='pa-branch-gantt__row pa-branch-gantt__row--axis'>
                                                                    <span className='pa-branch-gantt__label' />
                                                                    <div className='pa-branch-gantt__track pa-branch-gantt__track--axis'>
                                                                        {branchTimeline.cutoffs.map((cutoff) => (
                                                                            <div
                                                                                key={cutoff.percent}
                                                                                className='pa-branch-gantt__cutoff'
                                                                                style={{ left: `${cutoff.percent}%` }}
                                                                            >
                                                                                <span className='pa-branch-gantt__cutoff-label'>
                                                                                    <Moment format='DD/MM/YY'>{cutoff.date}</Moment>
                                                                                </span>
                                                                                <span className='pa-branch-gantt__cutoff-line' />
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {branchTimeline.uniqueBranches.map((branch) => (
                                                                <div key={branch.contractBranchId} className='pa-branch-gantt__row'>
                                                                    <Tooltip relationship='label' withArrow content={branch.contractBranchName}>
                                                                        <span className='pa-branch-gantt__label'>{branch.contractBranchName}</span>
                                                                    </Tooltip>
                                                                    <div className='pa-branch-gantt__track'>
                                                                        {branchTimeline.cutoffs.map((cutoff) => (
                                                                            <span
                                                                                key={cutoff.percent}
                                                                                className='pa-branch-gantt__cutoff-line pa-branch-gantt__cutoff-line--row'
                                                                                style={{ left: `${cutoff.percent}%` }}
                                                                            />
                                                                        ))}
                                                                        {patientHistory.map((branchItem, segmentIndex) => {
                                                                            if (branchItem.contractBranchId !== branch.contractBranchId) {
                                                                                return null;
                                                                            }

                                                                            // Highlight only the single assignment the patient is currently on (matches the header badge),
                                                                            // not every segment whose own date range happens to look "active" (avoids highlighting stale/open records).
                                                                            const isActive = branchItem.id === assignedBranch?.id;
                                                                            const isEditingItem = editingBranchAssignment?.id === branchItem.id;
                                                                            const leftPercent = segmentIndex * branchTimeline.segmentWidthPercent;
                                                                            const rangeLabel = `${new Date(branchItem.dateFrom).toLocaleDateString()} - ${branchItem.dateTo ? new Date(branchItem.dateTo).toLocaleDateString() : t('protocolActivity.branchAssignment.ongoing', { defaultValue: 'Actual' })}`;

                                                                            return (
                                                                                <Tooltip key={branchItem.id} relationship='label' withArrow content={`${branch.contractBranchName}: ${rangeLabel}`}>
                                                                                    <span
                                                                                        className={`pa-branch-gantt__bar ${isActive ? 'pa-branch-gantt__bar--active' : ''} ${isEditingItem ? 'pa-branch-gantt__bar--editing' : ''}`}
                                                                                        style={{ left: `${leftPercent}%`, width: `${branchTimeline.segmentWidthPercent}%` }}
                                                                                        onClick={() => onEditPatientBranch(patient, branchItem)}
                                                                                    >
                                                                                        {!branchItem.dateTo && (
                                                                                            <span className='pa-branch-gantt__bar-label'>
                                                                                                {t('protocolActivity.branchAssignment.ongoing', { defaultValue: 'Actual' })}
                                                                                            </span>
                                                                                        )}
                                                                                        <span className='pa-branch-gantt__bar-actions' onClick={(event) => event.stopPropagation()}>
                                                                                            <Button
                                                                                                appearance='transparent'
                                                                                                shape='circular'
                                                                                                size='small'
                                                                                                icon={<Edit16Regular />}
                                                                                                onClick={() => onEditPatientBranch(patient, branchItem)}
                                                                                                title={t('protocolActivity.branchAssignment.editAssignment', { defaultValue: 'Editar' })}
                                                                                            />
                                                                                            <ConfirmationModalComponent
                                                                                                onDismiss={() => {}}
                                                                                                onConfirm={() => onRemovePatientBranch(patient, branchItem.id)}
                                                                                                title={t('protocolActivity.branchAssignment.deleteAssignment')}
                                                                                                message={t('protocolActivity.branchAssignment.deleteConfirmMessage', { defaultValue: '¿Está seguro que desea eliminar esta asignación de rama?' })}
                                                                                                buttonApparence='transparent'
                                                                                                buttonShape='circular'
                                                                                                buttonTitle=''
                                                                                                buttonIcon={<Delete16Regular />}
                                                                                                isProcessing={savingBranchPatient === patient}
                                                                                            />
                                                                                        </span>
                                                                                    </span>
                                                                                </Tooltip>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>

                                                <Divider />

                                                <Caption1Stronger>
                                                    {editingBranchAssignment?.patient === patient
                                                        ? t('protocolActivity.branchAssignment.editTitle', { defaultValue: 'Editar asignación' })
                                                        : t('protocolActivity.branchAssignment.addNew')}
                                                </Caption1Stronger>
                                                {editingBranchAssignment?.patient === patient && (
                                                    <Button
                                                        appearance='transparent'
                                                        size='small'
                                                        onClick={() => clearPatientBranchEditingState(patient)}
                                                    >
                                                        {t('protocolActivity.branchAssignment.cancelEdit', { defaultValue: 'Cancelar edición' })}
                                                    </Button>
                                                )}
                                                {props.ContractId ? (
                                                    <>
                                                        <ContractBranchSelectorComponent
                                                            ContractId={props.ContractId}
                                                            ContractBranchesSelected={
                                                                selectedPatientBranchMap[patient] !== undefined
                                                                    ? selectedPatientBranchMap[patient]
                                                                    : selectedBranchForPicker
                                                            }
                                                            MultiselectEnable={false}
                                                            onSelectedContractBranch={(branches: ContractBranch[]) => {
                                                                setSelectedPatientBranchMap((prev) => ({
                                                                    ...prev,
                                                                    [patient]: branches
                                                                }));
                                                            }}
                                                            noteText={t('protocolActivity.branchAssignment.selectBranchLabel')}
                                                        />
                                                        <DateRangeSelectorComponent
                                                            startDate={patientBranchDateRange[patient]?.dateFrom}
                                                            endDate={patientBranchDateRange[patient]?.dateTo}
                                                            startDateLabel={t('protocolActivity.branchAssignment.dateFromLabel')}
                                                            endDateLabel={t('protocolActivity.branchAssignment.dateToLabel')}
                                                            onDateChange={(startDate, endDate) => {
                                                                setPatientBranchDateRange((prev) => ({
                                                                    ...prev,
                                                                    [patient]: { dateFrom: startDate, dateTo: endDate }
                                                                }));
                                                            }}
                                                        />
                                                    </>
                                                ) : (
                                                    <Text>{t('protocolActivity.branchAssignment.noContract')}</Text>
                                                )}
                                                <div className='pa-branch-popover__actions'>
                                                    <Button
                                                        appearance='secondary'
                                                        onClick={() => {
                                                            setBranchPopoverPatient(null);
                                                            clearPatientBranchEditingState(patient);
                                                        }}
                                                        disabled={savingBranchPatient === patient}
                                                    >
                                                        {t('protocolActivity.branchAssignment.cancel')}
                                                    </Button>
                                                    <Button
                                                        appearance='primary'
                                                        onClick={() => onSavePatientBranch(patient)}
                                                        disabled={
                                                            !props.ContractId ||
                                                            savingBranchPatient === patient ||
                                                            !selectedPatientBranchMap[patient]?.[0]?.id ||
                                                            !patientBranchDateRange[patient]?.dateFrom
                                                        }
                                                    >
                                                        {savingBranchPatient === patient
                                                            ? 'Guardando...'
                                                            : editingBranchAssignment?.patient === patient
                                                                ? t('protocolActivity.branchAssignment.update', { defaultValue: 'Actualizar' })
                                                                : t('protocolActivity.branchAssignment.save')}
                                                    </Button>
                                                </div>
                                            </div>
                                        </PopoverSurface>
                                    </Popover>
                                </th>
                                {displayStageColumns?.map((stage: DisplayStageColumn) => {
                                    const cellVisit = ComponentFunctions.GetVisitByPatientAndStage(patientVisits, patient, stage);
                                    const isPhoneVisit = cellVisit?.visitType === 1;
                                    // an extra-protocol visit exists only for the patients that had it:
                                    // where there is none the cell stays empty, with no marker at all.
                                    // Lo mismo vale para las notas.
                                    const isAdHocColumn = stage.isExtraProtocolVisit || stage.isNoteVisit;
                                    const hasMarker = !isAdHocColumn || !!cellVisit;

                                    return (
                                <td key={`cell-${patient}-${stage.columnKey}`} className={`${groupEndColumnKeys.has(stage.columnKey) ? 'pa-cell--group-end' : ''} ${stage.isOrphanBranch ? 'pa-cell--orphan-branch' : ''} ${stage.isExtraProtocolVisit ? 'pa-cell--extra' : ''} ${stage.isNoteVisit ? 'pa-cell--note' : ''}`}>
                                    {hasMarker && (
                                    <Popover withArrow>
                                        <PopoverTrigger disableButtonEnhancement>
                                            <div
                                                className={'VisitActivity '
                                                    // Una nota no tiene estado de visita: si tomara el
                                                    // color del status se leería como actividad.
                                                    .concat(cellVisit && !stage.isNoteVisit ? ComponentFunctions.GetVisitStatusByVisit(cellVisit.status) : '')
                                                    .concat(getOutOfContractClass(stage, !!cellVisit))
                                                    .concat(stage.isExtraProtocolVisit ? ' extra-visit' : '')
                                                    .concat(stage.isNoteVisit ? ' note-visit' : '')}
                                                title={stage.isNoteVisit
                                                    ? t('protocolActivity.table.noteVisit', { defaultValue: 'Anotación' })
                                                    : stage.isExtraProtocolVisit && cellVisit
                                                        ? (isPhoneVisit
                                                            ? t('protocolActivity.table.phoneVisit', { defaultValue: 'Visita telefónica / remota' })
                                                            : t('protocolActivity.table.onSiteVisit', { defaultValue: 'Visita presencial' }))
                                                        : undefined}
                                            >
                                                {stage.isNoteVisit
                                                    ? <Note16Regular />
                                                    : stage.isExtraProtocolVisit
                                                        // modality varies per patient here, so it is read from the visit itself
                                                        ? (cellVisit && (isPhoneVisit ? <Call16Regular /> : <Person16Regular />))
                                                        : (stage.isRemote &&
                                                            (cellVisit
                                                                ? (isPhoneVisit ? <Call16Regular /> : <Call16Filled style={{ color: '#b61111' }} />)
                                                                : <Call16Regular />))
                                                }{stage.remoteWarning && <Warning16Filled style={{ color: 'orange' }} />}
                                            </div>
                                        </PopoverTrigger>
                                        <PopoverSurface tabIndex={-1}>
                                            {VisitGrid(stage, cellVisit, protocolPatient || null)}
                                        </PopoverSurface>
                                    </Popover>
                                    )}
                                </td>
                                    );
                                })}
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
                );
            })}
            </div>
            </div>
            </div>
            {stageHeaderCallout && (
                <Callout
                    target={`#${stageHeaderCallout.targetId}`}
                    onDismiss={() => setStageHeaderCallout(null)}
                    directionalHint={DirectionalHint.bottomCenter}
                    gapSpace={8}
                    className='pa-stage-callout'
                >
                    <div className='pa-stage-callout__header'>
                        <Text className='pa-stage-callout__title'>
                            {t('protocolActivity.table.stageLabel', { defaultValue: 'Stage' })}: {stageHeaderCallout.stageTitle}
                        </Text>
                        <Text className='pa-stage-callout__subtitle'>{stageHeaderCallout.stageDescription}</Text>
                    </div>
                    <div className='pa-stage-callout__body'>
                        <Text className='pa-stage-callout__sectionTitle'>
                            {t('protocolActivity.table.flowchartRelationTitle', { defaultValue: 'Relación con flowchart' })}
                        </Text>
                        <div className='pa-stage-callout__graph'>
                            {stageHeaderCallout.relations.map((relation, index) => {
                                const relationParts = relation.split(' -> ');
                                const sourceLabel = relationParts[0] || stageHeaderCallout.stageTitle;
                                const targetLabel = relationParts[1] || t('protocolActivity.table.noFlowchartVisit', { defaultValue: 'Sin visita asociada' });

                                return (
                                    <div key={`${stageHeaderCallout.targetId}-relation-${index}`} className='pa-stage-callout__edgeRow'>
                                        <span className='pa-stage-callout__node pa-stage-callout__node--stage'>{sourceLabel}</span>
                                        <span className='pa-stage-callout__edge' aria-hidden='true'>
                                            <span className='pa-stage-callout__edgeLine' />
                                            <span className='pa-stage-callout__edgeArrow'>▶</span>
                                        </span>
                                        <span className='pa-stage-callout__node pa-stage-callout__node--visit'>{targetLabel}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </Callout>
            )}
            </>
            )}
            {visits?.length === 0 &&
                <div className='NoDataContainer'>
                    <img
                        src={require('../../../assets/img/nodata.png')}
                        alt="No Data"
                        style={{ height: '6em' }}
                    ></img>
                    <Text className='NoData'>No hay visitas realizadas</Text>
                </div>
            }
            {Form}
            {visitConceptDrawer && (
                <VisitConceptFormComponent
                    isOpen={!!visitConceptDrawer}
                    onClose={() => setVisitConceptDrawer(null)}
                    protocolId={props.ProtocolId}
                    visitId={visitConceptDrawer.visitId}
                    patientId={visitConceptDrawer.patientId}
                    ContractId={props.ContractId ?? 0}
                />
            )}
        </>
    )

}

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ProtocolActivityComponent as any);